import { useEffect, useState, useCallback } from 'react';

const TOUR_STORAGE_KEY = 'client_tour_completed_v1';

/**
 * useTour - manages onboarding tour state, completion persistence, and keyboard nav.
 * @param {object} opts
 * @param {Array} opts.steps - list of tour steps
 * @param {boolean} opts.isOpen - whether tour is currently visible
 * @param {Function} opts.onClose - called when tour ends or is skipped
 */
export const useTour = ({ steps, isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Reset to step 0 whenever tour reopens
  useEffect(() => {
    if (isOpen) setCurrentStep(0);
  }, [isOpen]);

  const goNext = useCallback(() => {
    setCurrentStep((s) => {
      if (s >= steps.length - 1) {
        // mark complete
        try { localStorage.setItem(TOUR_STORAGE_KEY, '1'); } catch (_) {}
        onClose && onClose('completed');
        return s;
      }
      return s + 1;
    });
  }, [steps.length, onClose]);

  const goPrev = useCallback(() => {
    setCurrentStep((s) => Math.max(0, s - 1));
  }, []);

  const skip = useCallback(() => {
    try { localStorage.setItem(TOUR_STORAGE_KEY, '1'); } catch (_) {}
    onClose && onClose('skipped');
  }, [onClose]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        skip();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, goNext, goPrev, skip]);

  return { currentStep, goNext, goPrev, skip, setCurrentStep };
};

export const hasCompletedTour = () => {
  try { return localStorage.getItem(TOUR_STORAGE_KEY) === '1'; } catch (_) { return false; }
};

export const resetTour = () => {
  try { localStorage.removeItem(TOUR_STORAGE_KEY); } catch (_) {}
};

export const TOUR_KEY = TOUR_STORAGE_KEY;
